import React, { useState } from 'react';
import { Plus, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { useFetch, fmtDate, statusStyles } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { ConfirmDialog } from '../_primitives/ConfirmDialog';
import type { CodeRow, InviteRow, ReferralRow } from '../types';

const codeCols = (onRevoke: (c: CodeRow) => void): Column<CodeRow>[] => [
  { key: 'code', label: 'Code', sortValue: (c) => c.code, searchValue: (c) => c.code,
    render: (c) => <span className="font-mono text-ink dark:text-white">{c.code}</span> },
  { key: 'note', label: 'Notitie', searchValue: (c) => c.note ?? '',
    render: (c) => <span className="text-ink/65 dark:text-white/65 truncate max-w-[24ch] inline-block" title={c.note ?? ''}>{c.note ?? '—'}</span> },
  { key: 'invited_company', label: 'Bedrijf', searchValue: (c) => c.invited_company ?? '',
    render: (c) => <span className="text-ink/65 dark:text-white/65">{c.invited_company ?? '—'}</span> },
  { key: 'created_at', label: 'Aangemaakt', sortValue: (c) => c.created_at,
    render: (c) => <span className="text-ink/55 dark:text-white/55">{fmtDate(c.created_at)}</span> },
  { key: 'expires_at', label: 'Verloopt', sortValue: (c) => c.expires_at ?? '',
    render: (c) => <span className="text-ink/55 dark:text-white/55">{fmtDate(c.expires_at)}</span> },
  { key: 'status', label: 'Status',
    render: (c) => {
      const status = c.revoked_at ? 'revoked' : c.used_at ? 'used' : 'active';
      return <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusStyles(status)}`}>{status}</span>;
    } },
  { key: '__actions', label: '',
    render: (c) => c.revoked_at ? null : (
      <button type="button" onClick={(e) => { e.stopPropagation(); onRevoke(c); }} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400 hover:underline">
        <ShieldOff size={11} /> intrek
      </button>
    ) },
];

const inviteCols: Column<InviteRow>[] = [
  { key: 'created_at', label: 'Datum', sortValue: (i) => i.created_at,
    render: (i) => <span className="text-ink/55 dark:text-white/55">{fmtDate(i.created_at)}</span> },
  { key: 'sender_email', label: 'Verzender', sortValue: (i) => i.sender_email ?? '', searchValue: (i) => i.sender_email ?? '',
    render: (i) => <span className="text-ink/75 dark:text-white/75">{i.sender_email ?? '—'}</span> },
  { key: 'recipient_email', label: 'Ontvanger', sortValue: (i) => i.recipient_email, searchValue: (i) => i.recipient_email,
    render: (i) => <span className="font-medium text-ink dark:text-white">{i.recipient_email}</span> },
  { key: 'type', label: 'Type', sortValue: (i) => i.type,
    render: (i) => <span className="text-ink/65 dark:text-white/65">{i.type}</span> },
  { key: 'status', label: 'Status',
    render: (i) => <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusStyles(i.status)}`}>{i.status}</span> },
];

const referralCols: Column<ReferralRow>[] = [
  { key: 'created_at', label: 'Datum', sortValue: (r) => r.created_at,
    render: (r) => <span className="text-ink/55 dark:text-white/55">{fmtDate(r.created_at)}</span> },
  { key: 'referrer_email', label: 'Verwijzer', sortValue: (r) => r.referrer_email ?? '', searchValue: (r) => r.referrer_email ?? '',
    render: (r) => <span className="text-ink/75 dark:text-white/75">{r.referrer_email ?? `#${r.referrer_id}`}</span> },
  { key: 'referred_email', label: 'Verwezen', sortValue: (r) => r.referred_email ?? '', searchValue: (r) => r.referred_email ?? '',
    render: (r) => <span className="font-medium text-ink dark:text-white">{r.referred_email ?? '—'}</span> },
  { key: 'status', label: 'Status',
    render: (r) => <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusStyles(r.status)}`}>{r.status}</span> },
];

interface Props {
  trigger?: { kind: 'create-code' | 'revoke-code' | null; nonce: number };
}

export const InvitesTab: React.FC<Props> = ({ trigger }) => {
  const codes = useFetch(adminApi.codes);
  const invites = useFetch(adminApi.sentInvites);
  const referrals = useFetch(adminApi.referrals);
  const [revoking, setRevoking] = useState<CodeRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState('');
  const [company, setCompany] = useState('');

  React.useEffect(() => {
    if (trigger?.kind === 'create-code') setCreating(true);
    if (trigger?.kind === 'revoke-code') {
      // ⌘K → revoke flow: surface inline picker via toast hint
      toast.info('Klik op een code-rij om in te trekken.');
    }
  }, [trigger]);

  const runCreate = async () => {
    try {
      await adminApi.createCode({ note: note || undefined, invitedCompany: company || undefined });
      toast.success('Toegangscode aangemaakt');
      setNote(''); setCompany('');
      setCreating(false);
      codes.refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aanmaken mislukt');
    }
  };

  const runRevoke = async () => {
    if (!revoking) return;
    try {
      await adminApi.revokeCode(revoking.id);
      toast.success(`Code ${revoking.code} ingetrokken`);
      codes.refresh();
      setRevoking(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Intrekken mislukt');
    }
  };

  return (
    <div className="space-y-4">
      <Section
        title={`Toegangscodes (${(codes.data ?? []).length})`}
        toolbar={
          <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-accent hover:bg-accent-hover text-white text-xs font-semibold transition-colors">
            <Plus size={12} /> Nieuwe code
          </button>
        }
      >
        <DataTable
          rows={codes.data ?? []}
          loading={codes.loading}
          error={codes.error}
          onRetry={codes.refresh}
          columns={codeCols(setRevoking)}
          rowKey={(c) => c.id}
          searchPlaceholder="Zoek op code, notitie, bedrijf…"
        />
      </Section>

      <Section title={`Verzonden invites (${(invites.data ?? []).length})`}>
        <DataTable
          rows={invites.data ?? []}
          loading={invites.loading}
          error={invites.error}
          onRetry={invites.refresh}
          columns={inviteCols}
          rowKey={(i) => i.id}
          searchPlaceholder="Zoek op verzender of ontvanger…"
        />
      </Section>

      <Section title={`Referrals (${(referrals.data ?? []).length})`}>
        <DataTable
          rows={referrals.data ?? []}
          loading={referrals.loading}
          error={referrals.error}
          onRetry={referrals.refresh}
          columns={referralCols}
          rowKey={(r) => r.id}
          searchPlaceholder="Zoek op verwijzer of verwezen…"
        />
      </Section>

      <ConfirmDialog
        open={creating}
        title="Nieuwe toegangscode"
        description={
          <div className="space-y-3">
            <input type="text" placeholder="Notitie (optioneel)" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
            <input type="text" placeholder="Bedrijf (optioneel)" value={company} onChange={(e) => setCompany(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/15 bg-white dark:bg-ink/60 dark:text-white focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20" />
          </div>
        }
        confirmLabel="Aanmaken"
        onConfirm={runCreate}
        onCancel={() => setCreating(false)}
      />

      <ConfirmDialog
        open={!!revoking}
        destructive
        title="Toegangscode intrekken"
        description={revoking && <p>Code <span className="font-mono">{revoking.code}</span> wordt onbruikbaar. Dit is permanent.</p>}
        confirmLabel="Intrek"
        onConfirm={runRevoke}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
};
